/**
 * My Profile page.
 *
 * Reads the current user via `GET /users/me` and saves edits via
 * `PATCH /users/me`, then resyncs the shared `AuthContext` user so the
 * sidebar/dashboard reflect the change immediately.
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, Box, Button, Chip, Divider, Stack, TextField, Typography } from "@mui/material";
import userService from "@/services/userService";
import useAuth from "@/hooks/useAuth";
import { extractErrorMessage } from "@/utils/errors";
import type { User } from "@/types";

interface ProfileFormValues {
  full_name: string;
  phone_number: string;
}

export function ProfilePage() {
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileFormValues>();

  useEffect(() => {
    userService
      .getMyProfile()
      .then((data) => {
        setProfile(data);
        reset({ full_name: data.full_name, phone_number: data.phone_number ?? "" });
      })
      .catch((err) => setLoadError(extractErrorMessage(err)));
  }, [reset]);

  const onSubmit = async (values: ProfileFormValues) => {
    setServerError(null);
    setSuccess(false);
    setIsSubmitting(true);
    try {
      const updated = await userService.updateMyProfile({
        full_name: values.full_name,
        phone_number: values.phone_number || undefined,
      });
      setProfile(updated);
      setSuccess(true);
      await refreshUser();
    } catch (err) {
      setServerError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  return (
    <Box maxWidth={480}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        My Profile
      </Typography>

      {profile && (
        <Stack direction="row" spacing={1} mb={3}>
          {profile.roles.map((role) => (
            <Chip key={role.id} label={role.name} size="small" />
          ))}
          <Chip
            label={profile.is_active ? "Active" : "Inactive"}
            color={profile.is_active ? "success" : "default"}
            size="small"
          />
        </Stack>
      )}

      <Divider sx={{ mb: 3 }} />

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2}>
          {serverError && <Alert severity="error">{serverError}</Alert>}
          {success && <Alert severity="success">Profile updated successfully.</Alert>}

          <TextField label="Email" value={profile?.email ?? ""} disabled fullWidth />

          <TextField
            label="Full name"
            fullWidth
            error={Boolean(errors.full_name)}
            helperText={errors.full_name?.message}
            {...register("full_name", { required: "Full name is required", minLength: 2 })}
          />

          <TextField label="Phone number" fullWidth {...register("phone_number")} />

          <Box>
            <Button type="submit" variant="contained" disabled={isSubmitting || !profile}>
              {isSubmitting ? "Saving..." : "Save changes"}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

export default ProfilePage;
